import { datadogRum } from "@datadog/browser-rum";
import { Box, Tooltip, Typography, useTheme } from "@mui/material";
import { DayOfWeek } from "@notemeal/graphql/types";
import { grayBackground } from "@notemeal/palette";
import { useDateFormatting } from "@notemeal/shared/ui/contexts/useDateFormatting";
import { capitalize } from "@notemeal/shared/ui/utils/capitalize";
import { ConfirmationDialog } from "apps/web/src/componentLibrary";
import { useSnackbar } from "apps/web/src/components/Snackbar/SnackbarContext";
import { addDays, format } from "date-fns";
import React, { useState } from "react";
import { UseFieldArrayReturn, UseFormReturn } from "react-hook-form";
import { useMenuBuilderContext } from "../MenuBuilderProvider";
import { MenuBuilderColumnToolbar } from "./MenuBuilderColumnToolbar";
import { MenuBuilderMealGridRow } from "./MenuBuilderMealGridRow";
import { MenuBuilderType } from "./MenuBuilderMealSchema";
import { DAY_CELL_WIDTH, LEFT_PANEL_CELL_WIDTH } from "./constants";

interface MenuBuilderMealProps {
  rows: UseFieldArrayReturn<MenuBuilderType>;
  form: UseFormReturn<MenuBuilderType>;
  weekFormIndex: number;
  mealFormIndex: number;
}

export const MenuBuilderMealGrid = ({ rows, form, mealFormIndex, weekFormIndex }: MenuBuilderMealProps) => {
  const {
    days,
    weeks,
    selectedWeekIndex,
    setRowsToDelete,
    rowsToDelete,
    menuId,
    disabledDays,
    setDisabledDays,
    selectedDay,
    setSelectedDay,
  } = useMenuBuilderContext();
  const [toolbarPosition, setToolbarPosition] = useState<{ top: number; left: number } | undefined>(undefined);
  const [deleteMenuItemDialogOpen, setDeleteMenuItemDialogOpen] = useState(false);
  const {
    palette: { background, info, greyscale },
  } = useTheme();
  const { setMessage } = useSnackbar();
  const { formatYearlessDateWithLocale } = useDateFormatting();

  const { id: currentWeekId, startDate } = weeks[selectedWeekIndex];

  const deleteRow = (rowFormIndex: number) => {
    datadogRum.addAction("menu_builder.removed_dining_station", { menuId });
    // only delete rows that previously existed
    const rowId = form.getValues(`weeks.${weekFormIndex}.meals.${mealFormIndex}.rows.${rowFormIndex}`).id;
    if (rowId) {
      setRowsToDelete([...rowsToDelete, rowId]);
    }
    rows.remove(rowFormIndex);
  };
  const currentWeeksDisabledDays = disabledDays.filter(d => d.plannedMenuWeekId === currentWeekId).flatMap(d => d.disabledDays);
  const isDayEnabled = (day: DayOfWeek) => !currentWeeksDisabledDays.includes(day);

  const displayDayOfWeek = (day: DayOfWeek) => {
    return `${formatYearlessDateWithLocale(addDays(startDate, days.indexOf(day)))} (${capitalize(day)})`;
  };

  const disableDay = (day: DayOfWeek) => {
    datadogRum.addAction("menu_builder.disable_day", { menuId });
    setDisabledDays([
      ...disabledDays.filter(d => d.plannedMenuWeekId !== currentWeekId),
      {
        plannedMenuWeekId: currentWeekId,
        disabledDays: [...currentWeeksDisabledDays, day],
      },
    ]);
    setDeleteMenuItemDialogOpen(false);
    closeToolbar();
    setMessage("success", `Disabled ${displayDayOfWeek(day)}`);
  };

  const maybeDisableDay = (day: DayOfWeek) => {
    const itemsForDay = form
      .getValues(`weeks.${weekFormIndex}.meals`)
      .flatMap(meal => meal.rows)
      .flatMap(row => row.items)
      .filter(item => item.dayOfWeek === day);
    if (itemsForDay.length === 0) {
      disableDay(day);
    } else {
      setDeleteMenuItemDialogOpen(true);
    }
  };

  const enableDay = (day: DayOfWeek) => {
    datadogRum.addAction("menu_builder.enable_day", { menuId });
    setDisabledDays([
      ...disabledDays.filter(d => d.plannedMenuWeekId !== currentWeekId),
      {
        plannedMenuWeekId: currentWeekId,
        disabledDays: currentWeeksDisabledDays.filter(d => d !== day),
      },
    ]);
    closeToolbar();
    setMessage("success", `Enabled ${displayDayOfWeek(day)}`);
  };

  const getBackgroundAndBorder = (day: DayOfWeek) => {
    if (!isDayEnabled(day)) {
      return { backgroundColor: greyscale[200], borderColor: greyscale[300], borderWidth: "0px 1px 1px 0px" };
    } else if (selectedDay === day) {
      return { backgroundColor: info.lighter, borderColor: info.main, borderWidth: "1px" };
    } else {
      return { backgroundColor: background.default, borderColor: greyscale[300], borderWidth: "0px 1px 1px 0px" };
    }
  };

  const closeToolbar = () => {
    setToolbarPosition(undefined);
    setSelectedDay(undefined);
  };

  return (
    <Box>
      {/* header with days */}
      <Box sx={{ display: "grid" }}>
        <Box
          sx={{
            backgroundColor: grayBackground,
            minWidth: LEFT_PANEL_CELL_WIDTH,
            height: "50px",
            borderWidth: "0px 1px 1px 1px",
            borderStyle: "solid",
            borderColor: greyscale[300],
          }}
        ></Box>
        {days.map((day, index) => (
          <Tooltip
            title={
              isDayEnabled(day) ? (
                ""
              ) : (
                <>
                  <Typography sx={{ color: "white" }} variant="subtitle1Semibold">
                    {`Disabled ${displayDayOfWeek(day)}`}
                  </Typography>
                  <Typography sx={{ color: "white", width: "197px" }} variant="subtitle1">
                    No meals will be available for order and it will not be visible to athletes
                  </Typography>
                </>
              )
            }
          >
            <Box
              key={day}
              sx={{
                ...getBackgroundAndBorder(day),
                borderStyle: "solid",
                gridColumn: `${index + 2}`,
                width: DAY_CELL_WIDTH,
                height: "50px",
                justifyContent: "center",
                alignContent: "center",
                display: "grid",
                "&:hover": { cursor: "pointer" },
              }}
              onClick={e => {
                setToolbarPosition({ top: e.clientY, left: e.clientX });
                setSelectedDay(day);
              }}
            >
              <Box>
                <Typography display="inline" variant="body2">
                  {capitalize(day)}{" "}
                </Typography>
                <Typography variant="body2Semibold">{format(addDays(startDate, index), "d")}</Typography>
              </Box>
            </Box>
          </Tooltip>
        ))}
      </Box>
      {/* cells for rows with menu items */}
      {rows.fields.map((row, index) => (
        <MenuBuilderMealGridRow
          key={row.id}
          form={form}
          weekFormIndex={weekFormIndex}
          mealFormIndex={mealFormIndex}
          rowFormIndex={index}
          deleteRow={deleteRow}
          currentWeeksDisabledDays={currentWeeksDisabledDays}
        />
      ))}

      {selectedDay && (
        <>
          <MenuBuilderColumnToolbar
            position={toolbarPosition}
            onClickDisable={() => maybeDisableDay(selectedDay)}
            onClickEnable={() => enableDay(selectedDay)}
            isDayEnabled={isDayEnabled(selectedDay)}
            onClose={closeToolbar}
          />
          <ConfirmationDialog
            open={deleteMenuItemDialogOpen}
            title="Delete Menu Items"
            message={`Are you sure you want to disable the column? Any menu items that have been added to the date will be deleted.`}
            onCancel={() => {
              datadogRum.addAction("menu_builder.cancelled_disable_day", { menuId });
              setDeleteMenuItemDialogOpen(false);
              closeToolbar();
            }}
            onConfirm={() => disableDay(selectedDay)}
            confirmLabel="Yes"
            variant="containedDestructive"
          />
        </>
      )}
    </Box>
  );
};
